import type Keycloak from "keycloak-js";

// POST /users/me/switch-account writes the new org to the user's Keycloak
// attributes, but the access token in hand still carries the old org_id claim
// until it is refreshed — and Keycloak needs a moment to make that write visible
// to the refresh grant. Nothing the client sends scopes a request to an org: the
// gateway derives it from this claim. So the switch is not complete, and the UI
// must not move, until the claim reflects it.
//
// Each attempt is a forced refresh-grant, and the realm rotates refresh tokens
// (revokeRefreshToken with refreshTokenMaxReuse 0), so these are kept few and
// spaced out rather than hammered: the token in hand is checked before any
// refresh, and the backoff widens so a slow attribute write is waited out
// instead of being retried into.
export const ORG_CLAIM_BACKOFF_MS = [150, 350, 700, 1200];

/** Minimal surface of the Keycloak instance this needs — keeps it testable. */
export interface RefreshableToken {
  updateToken: (minValidity: number) => Promise<boolean>;
  tokenParsed?: Keycloak["tokenParsed"];
}

const claims = (kc: RefreshableToken): Record<string, unknown> | undefined =>
  kc.tokenParsed as Record<string, unknown> | undefined;

/**
 * Ensure the JWT in hand carries the org just switched to, refreshing until it
 * does. Returns false if it never converges, which means the caller must not
 * present the new org — the token would still fetch the old one's data.
 *
 * A refresh that throws ends this immediately rather than retrying. Under
 * refresh-token rotation a failed refresh may have rotated server-side without
 * the client seeing the new token, so a retry would replay a spent token and
 * Keycloak's reuse detection would kill the session. Returning false costs a
 * reload, which re-runs SSO and recovers; retrying can cost the session.
 */
export async function waitForOrgClaim(kc: RefreshableToken, targetOrgId: string): Promise<boolean> {
  // The token in hand may already be current — switching back to the org a
  // still-valid token was minted for needs no refresh at all.
  if (claims(kc)?.org_id === targetOrgId) return true;

  for (const delayMs of ORG_CLAIM_BACKOFF_MS) {
    await new Promise((r) => setTimeout(r, delayMs));
    try {
      await kc.updateToken(-1);
    } catch {
      return false;
    }
    if (claims(kc)?.org_id === targetOrgId) return true;
  }
  return false;
}
